import { IonApp, IonLoading, IonRouterOutlet, IonToast, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import "./theme/sispoweb/variables.css";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ForegroundNotification } from "./components/foregroundNotification/foregroundNotification";
import { RouteNotFound } from "./components/returnLoginComponent/returncomponentLogin";
import { AppRoutes } from "./constants/appRoutes";
import { AboutSecurityContainer } from "./pages/aboutSecurity/data/container";
import LoginContainer from "./pages/login/container";
import { AppRoute } from "./pages/request/component";
import { ILocalStorageUserData, getUserClaimsAction } from "./redux/actions/user/user";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { setUserClaims } from "./redux/reducers/signup/claims";
import { setUserSignupData } from "./redux/reducers/signup/form";
import { setupAxiosInterceptors } from "./services/axiosInterceptor";
import { LocalStorageKey, LocalStorageService } from "./services/localStorageService";
import { useCheckNotificationPermission } from "./services/notificationPermissions";
import { useAvailableNotification } from "./services/notificationUserRequeriment";
import { isCompatibleBrowserPushNotification } from "./services/pushnotification";
import { AboutIOSContainer } from "./pages/aboutBrowser/data/container";
import { EnvironmentType } from "./constants/environtmentType";
import { resetErrorOnRequest } from "./redux/reducers/api/errorOnRequest";
import { Milliseconds } from "./constants/milliseconds";

setupIonicReact();

const App: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common");

    const { loading } = useAppSelector((state) => state.globalLoading);
    const { isVisible, message } = useAppSelector((state) => state.errorOnRequest);
    const localStorage = new LocalStorageService();
    const login: ILocalStorageUserData = JSON.parse(localStorage.getItem(LocalStorageKey.USER_SIGNUP));
    const token: string = localStorage.getItem(LocalStorageKey.TOKEN);

    setupAxiosInterceptors(async () => {
        await dispatch(getUserClaimsAction());
    });

    useCheckNotificationPermission();
    useAvailableNotification();

    useEffect(() => {
        if (login) {
            dispatch(setUserClaims(token));
            dispatch(setUserSignupData({ organizationId: login.organizationId, phoneNumber: login.phoneNumber }));
        }
    }, [login]);

    const environment = process.env.REACT_APP_ENVIRONMENT;

    useEffect(() => {
        if (environment === EnvironmentType.DEV || environment === EnvironmentType.PRE) {
            const meta = document.createElement("meta");
            meta.name = "robots";
            meta.content = "noindex, nofollow";
            document.head.appendChild(meta);
        }
    }, [environment]);

    return (
        <IonApp>
            <IonRouterOutlet animated={true}>
                <IonReactRouter>
                    <Route
                        exact
                        path={"/"}
                        render={() => (login ? <Redirect to={AppRoutes.REQUEST} /> : <Redirect to={AppRoutes.SIGNUP} />)}
                    />
                    <Route
                        exact
                        path={AppRoutes.SIGNUP}
                        component={LoginContainer}
                    />
                    <Route
                        path={AppRoutes.REQUEST}
                        render={(props) => <AppRoute {...props} />}
                    />
                    <Route
                        exact
                        path={AppRoutes.ABOUT_SECURITY}
                        component={AboutSecurityContainer}
                    />
                    <Route
                        exact
                        path={AppRoutes.ABOUT_IOS}
                        component={AboutIOSContainer}
                    />
                    <Route
                        path={"*"}
                        component={RouteNotFound}
                    />
                    {isCompatibleBrowserPushNotification() && <ForegroundNotification />}
                </IonReactRouter>
            </IonRouterOutlet>
            <IonLoading
                message={t("GLOBAL.LOADING") ?? ""}
                spinner="circular"
                isOpen={loading}
            />
            <IonToast
                isOpen={isVisible}
                message={message}
                duration={Milliseconds.TEN_SEC}
                onDidDismiss={() => dispatch(resetErrorOnRequest())}
                onWillDismiss={() => dispatch(resetErrorOnRequest())}
            />
        </IonApp>
    );
};

export default App;
